import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { ArrowLeftIcon, CubeIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useProductDetail, useUpdateProduct } from '@/hooks/useProducts';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ProductImage } from '@/components/ui/ProductImage';
import { formatCurrency } from '@/utils/format';

export function ProductDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId: pidStr } = useParams();
  const productId = Number(pidStr);
  const activeSiteId = useUIStore((s) => s.activeSiteId);

  const { data: product, isLoading, isError, refetch } = useProductDetail(activeSiteId, isNaN(productId) ? -1 : productId);
  const updateMutation = useUpdateProduct(activeSiteId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ regularPrice: '', salePrice: '', stockQuantity: '', stockStatus: '', status: '' });
  const [editError, setEditError] = useState('');
  const [imageModal, setImageModal] = useState<string | null>(null);

  if (!activeSiteId) {
    return <EmptyState icon={CubeIcon} title={t('dashboard.selectStore')} />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (isNaN(productId) || isError || !product) return <ErrorState onRetry={() => refetch()} />;

  const openEdit = () => {
    setEditError('');
    setEditForm({ regularPrice: product.regularPrice || product.price, salePrice: product.salePrice || '', stockQuantity: product.stockQuantity != null ? String(product.stockQuantity) : '', stockStatus: product.stockStatus || 'instock', status: product.status });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setEditError('');
    if (editForm.regularPrice && editForm.salePrice && Number(editForm.salePrice) >= Number(editForm.regularPrice)) {
      setEditError(t('products.salePriceMustBeLower'));
      return;
    }
    if (editForm.stockQuantity !== '' && Number(editForm.stockQuantity) < 0) {
      setEditError(t('products.stockCannotBeNegative'));
      return;
    }
    try {
      await updateMutation.mutateAsync({
        productId: product.id,
        data: { regular_price: editForm.regularPrice || undefined, sale_price: editForm.salePrice, stock_quantity: editForm.stockQuantity !== '' ? Number(editForm.stockQuantity) : undefined, stock_status: editForm.stockStatus as 'instock' | 'outofstock' | 'onbackorder' || undefined, status: editForm.status as 'publish' | 'draft' },
      });
      setEditOpen(false);
    } catch {
      // Error toast shown by mutation hook
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')} leftIcon={<ArrowLeftIcon className="h-4 w-4" />}>{t('common.back')}</Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{product.name}</h1>
        <Badge label={product.status === 'publish' ? t('products.published') : t('products.draft')} variant={product.status === 'publish' ? 'success' : 'default'} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Product Info */}
          <Card title={t('products.productInfo')}>
            <div className="flex gap-6">
              <ProductImage src={product.images?.[0]?.src || null} alt={product.images?.[0]?.alt || product.name} size="h-48 w-48" iconSize="h-10 w-10" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{product.name}</h2>
                {product.sku && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('products.sku')}: {product.sku}</p>}
                {product.shortDescription && <div className="mt-3 text-sm text-gray-600 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.shortDescription) }} />}
              </div>
            </div>
            {product.description && (
              <div className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }} />
            )}
          </Card>

          {/* Pricing */}
          <Card title={t('products.pricing')} action={<Button variant="ghost" size="sm" onClick={openEdit}>{t('common.edit')}</Button>}>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">{t('products.regularPrice')}</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(product.regularPrice)}</p>
              </div>
              {product.salePrice && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400">{t('products.salePrice')}</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(product.salePrice)}</p>
                </div>
              )}
              <div>
                <p className="text-gray-500 dark:text-gray-400">{t('products.totalSales')}</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{product.totalSales}</p>
              </div>
            </div>
          </Card>

          {/* Categories & Tags */}
          <Card title={t('products.categories')}>
            <div className="space-y-3">
              {product.categories.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">{t('products.categories')}</p>
                  <div className="flex flex-wrap gap-1">{product.categories.map((c) => <Badge key={c.id} label={c.name} />)}</div>
                </div>
              )}
              {product.tags.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">{t('products.tags')}</p>
                  <div className="flex flex-wrap gap-1">{product.tags.map((t_) => <Badge key={t_.id} label={t_.name} variant="info" />)}</div>
                </div>
              )}
              {product.categories.length === 0 && product.tags.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">-</p>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          {/* Status */}
          <Card title={t('products.status')}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('products.status')}</span>
                <Badge label={product.status === 'publish' ? t('products.published') : t('products.draft')} variant={product.status === 'publish' ? 'success' : 'default'} />
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('products.productType')}</span>
                <span className="text-gray-900 dark:text-gray-100">{product.type === 'simple' ? t('products.simple') : t('products.variable')}</span>
              </div>
              {product.sku && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('products.sku')}</span>
                  <span className="text-gray-900 dark:text-gray-100">{product.sku}</span>
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={openEdit}>{t('common.edit')}</Button>
          </Card>

          {/* Inventory */}
          <Card title={t('products.inventory')}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('products.stockStatus')}</span>
                <Badge label={product.stockStatus === 'instock' ? t('products.inStock') : product.stockStatus === 'outofstock' ? t('products.outOfStock') : t('products.lowStock')} variant={product.stockStatus === 'instock' ? 'success' : product.stockStatus === 'outofstock' ? 'error' : 'warning'} />
              </div>
              {product.stockQuantity != null && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('products.stockQuantity')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{product.stockQuantity}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('products.manageStock')}</span>
                <span className="text-gray-900 dark:text-gray-100">{product.manageStock ? t('common.yes') : t('common.no')}</span>
              </div>
            </div>
          </Card>

          {/* Images */}
          {product.images.length > 0 && (
            <Card title={t('products.images')}>
              <div className="grid grid-cols-3 gap-2">
                {product.images.map((img) => (
                  <div key={img.id} className="cursor-pointer transition-opacity hover:opacity-80" onClick={() => setImageModal(img.src)}>
                    <ProductImage src={img.src} alt={img.alt} size="h-auto w-full aspect-square" iconSize="h-8 w-8" />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title={t('products.quickEdit')} size="sm">
        <div className="space-y-4">
          {editError && <p className="text-sm text-red-500">{editError}</p>}
          <Input label={t('products.regularPrice')} type="number" step="0.01" min="0" value={editForm.regularPrice} onChange={(e) => setEditForm((f) => ({ ...f, regularPrice: e.target.value }))} />
          <Input label={t('products.salePrice')} type="number" step="0.01" min="0" value={editForm.salePrice} onChange={(e) => setEditForm((f) => ({ ...f, salePrice: e.target.value }))} placeholder={t('products.leaveEmptyToRemoveSale')} />
          <Input label={t('products.stockQuantity')} type="number" min="0" value={editForm.stockQuantity} onChange={(e) => setEditForm((f) => ({ ...f, stockQuantity: e.target.value }))} />
          <Select label={t('products.stockStatus')} options={[{ value: 'instock', label: t('products.inStock') }, { value: 'outofstock', label: t('products.outOfStock') }, { value: 'onbackorder', label: t('products.onBackorder') }]} value={editForm.stockStatus} onChange={(v) => setEditForm((f) => ({ ...f, stockStatus: v }))} />
          <Select label={t('products.status')} options={[{ value: 'publish', label: t('products.published') }, { value: 'draft', label: t('products.draft') }]} value={editForm.status} onChange={(v) => setEditForm((f) => ({ ...f, status: v }))} />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} loading={updateMutation.isPending}>{t('common.save')}</Button>
          </div>
        </div>
      </Modal>

      {/* Image Lightbox */}
      <Modal isOpen={!!imageModal} onClose={() => setImageModal(null)} title="" size="lg">
        {imageModal && <ProductImage src={imageModal} alt="" size="w-full h-auto" iconSize="h-12 w-12" />}
      </Modal>
    </div>
  );
}
